import { Message } from '../../type';
import { useCareTeamSelector } from './useCareTeamSelector';
import { useCallback } from 'react';
import { useCurrentUserGuid } from '../../../../firebase/useCollection';
import { FileUploadType } from '../../store/types';

export const useMessages = (channelGuid: string): Message[] | null => {
  const messages = useCareTeamSelector(
    useCallback(
      (state) => {
        return state === 'LOADING' ? [] : state.messages[channelGuid] ?? null;
      },
      [channelGuid],
    ),
  );
  return messages ? [...messages] : null;
};

export const useCleanMessages = (channelGuid: string): Message[] | null => {
  const currentUserGuid = useCurrentUserGuid();
  const messages = useMessages(channelGuid);
  if (!messages) {
    return null;
  }
  return messages.filter((message) => {
    const { messageType, authorGuid, scanStatus, compressError, mediaPath } =
      message;
    if (!messageType || messageType === 'TEXT') {
      return true;
    }
    if (
      currentUserGuid !== authorGuid &&
      (scanStatus !== 'CLEAN' || compressError)
    ) {
      return false;
    }
    return !!mediaPath;
  });
};

export const useFileMessages = (
  channelGuid: string,
  fileUploadType: FileUploadType,
): Message[] | null => {
  const messages = useCareTeamSelector(
    useCallback(
      (state) => {
        if (state === 'LOADING') {
          return [];
        }
        const byChannel =
          fileUploadType === 'MEDIA'
            ? state.mediaMessages
            : state.rawFileMessages;
        return byChannel[channelGuid] ?? null;
      },
      [channelGuid, fileUploadType],
    ),
  );
  return messages ? [...messages] : null;
};

export const useOldestMessages = (channelGuid: string): Message | null => {
  const messages = useMessages(channelGuid);
  return messages?.[messages.length - 1] ?? null;
};

export const useOldestFileMessages = (
  channelGuid: string,
  fileUploadType: FileUploadType,
): Message | null => {
  const messages = useFileMessages(channelGuid, fileUploadType);
  return messages?.[messages.length - 1] ?? null;
};

export const useHasMoreMessages = (channelGuid: string): boolean => {
  return useCareTeamSelector(
    useCallback(
      (state) => {
        return state === 'LOADING'
          ? true
          : state.channelMetadata[channelGuid]?.hasMore ?? true;
      },
      [channelGuid],
    ),
  );
};

export const useHasMoreFileMessages = (
  channelGuid: string,
  fileUploadType: FileUploadType,
): boolean => {
  return useCareTeamSelector(
    useCallback(
      (state) => {
        if (state === 'LOADING') {
          return true;
        }
        const metadata = state.channelMetadata[channelGuid];
        const hasMore =
          fileUploadType === 'MEDIA'
            ? metadata?.hasMoreMediaMessages
            : metadata?.hasMoreRawFileMessages;
        return hasMore ?? true;
      },
      [channelGuid, fileUploadType],
    ),
  );
};
